/**
 * InstallPwaPrompt — dismissible "Add to Home Screen" card.
 *
 * Iter 83 (Feb 27 2026). Mobile-only. Renders a soft bottom card that
 * walks the user through saving TruBeauté to their home screen. Two
 * platform branches:
 *
 *   • Android (Chrome / Edge / Samsung Internet): listen for the
 *     `beforeinstallprompt` event the browser fires when the page is
 *     eligible for install. We intercept it, stash the event, and show
 *     OUR card so the user has context BEFORE the OS dialog. Tapping
 *     "Install" replays the deferred event to open the native picker.
 *
 *   • iOS Safari: there is no `beforeinstallprompt` — Safari requires
 *     the user to Share → "Add to Home Screen" manually. We render an
 *     iOS-specific instruction card with the exact tap sequence.
 *
 * The card NEVER shows when:
 *   • the app is already running standalone (display-mode standalone
 *     or window.navigator.standalone === true on iOS),
 *   • the user dismissed it before (localStorage flag),
 *   • the user is in stealth mode (we keep their footprint minimal).
 *
 * We also wait 12s after mount before showing — the founder's brief:
 * "shown only after the user has engaged with the app." 12s is a
 * conservative engagement floor (long enough to scroll the hero,
 * short enough to catch a quick browser).
 *
 * Analytics (first-party): pwa_install_prompt_shown / _dismissed /
 * pwa_install_accepted / pwa_install_declined / pwa_installed.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Share, MoreVertical, Plus, X, Sparkles, ArrowDown } from "lucide-react";

import { trackFP } from "../lib/firstPartyAnalytics";

const DISMISS_KEY = "trubeaute_pwa_dismissed_v1";
const ENGAGEMENT_DELAY_MS = 12_000;

// Phase 4 (Feb 27 2026): only show the install prompt on the public
// landing surfaces. Deep app pages (capture, routine, AR mirror,
// admin, billing, auth) are not where a "save us to your home screen"
// pitch belongs — the user has already chosen to engage.
const LANDING_ROUTE_RE = /^(\/(fr|es|pt))?\/?(start)?$/;

// `display-mode: standalone` is the canonical detection; iOS Safari
// exposes `navigator.standalone` instead.
function isRunningStandalone() {
  try {
    if (
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches
    ) {
      return true;
    }
  } catch {
    /* matchMedia not available */
  }
  return Boolean(window.navigator && window.navigator.standalone);
}

function detectIOSSafari() {
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  // Match Safari but exclude Chrome / Firefox / Edge on iOS which
  // identify with CriOS / FxiOS / EdgiOS in the UA. The other browsers
  // on iOS also cannot install PWAs, but for THEM we just hide the
  // prompt entirely (no instructions would help — Safari is required).
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome/i.test(ua);
  return isIOS && isSafari;
}

const InstallPwaPrompt = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState(null); // "android" | "ios" | null
  const [visible, setVisible] = useState(false);

  // Landing-only gate. The prompt is allowed on `/`, `/start`, and
  // their locale-prefixed siblings. Any other route hides it.
  const onLanding = LANDING_ROUTE_RE.test(location.pathname);

  // Read dismissal + standalone once on mount. We don't watch storage
  // changes — if the user dismissed in one tab they shouldn't be
  // re-prompted in another within the same session.
  useEffect(() => {
    if (!onLanding) return;
    // Hard exits first.
    if (isRunningStandalone()) return;
    try {
      if (localStorage.getItem(DISMISS_KEY)) return;
      if (localStorage.getItem("trubeaute_stealth") === "1") return;
    } catch {
      /* localStorage blocked — proceed but we can't remember dismissal */
    }

    const iosSafari = detectIOSSafari();

    // Android / Chromium path: wait for `beforeinstallprompt`. If it
    // fires we stash the event and arm a delayed reveal.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform("android");
      window.setTimeout(() => {
        setVisible(true);
        trackFP("pwa_install_prompt_shown", { platform: "android" });
      }, ENGAGEMENT_DELAY_MS);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari path: no event will fire — schedule the reveal
    // unconditionally so iOS users see instructions.
    let iosTimer;
    if (iosSafari) {
      iosTimer = window.setTimeout(() => {
        setPlatform("ios");
        setVisible(true);
        trackFP("pwa_install_prompt_shown", { platform: "ios" });
      }, ENGAGEMENT_DELAY_MS);
    }

    // Listen for the actual install completion so we hide ourselves
    // and record the conversion.
    const onAppInstalled = () => {
      try { localStorage.setItem(DISMISS_KEY, "installed"); } catch { /* noop */ }
      trackFP("pwa_installed");
      setVisible(false);
    };
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, [onLanding]);

  const persistDismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    } catch {
      /* noop */
    }
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === "accepted") {
        trackFP("pwa_install_accepted");
      } else {
        trackFP("pwa_install_declined");
      }
    } catch {
      /* user canceled the OS picker — non-fatal */
    }
    persistDismiss();
    setDeferredPrompt(null);
    setVisible(false);
  }, [deferredPrompt, persistDismiss]);

  const handleDismiss = useCallback(() => {
    persistDismiss();
    trackFP("pwa_install_prompt_dismissed", { platform: platform || "unknown" });
    setVisible(false);
  }, [persistDismiss, platform]);

  if (!visible || !platform || !onLanding) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="pwa-prompt"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        className="fixed left-3 right-3 bottom-3 z-[80] mx-auto max-w-md lg:hidden"
        role="dialog"
        aria-label={t("pwa.ariaLabel")}
        data-testid="pwa-install-prompt"
      >
        <div className="relative rounded-2xl bg-white border border-[#EADDD7] shadow-[0_18px_44px_rgba(155,106,108,0.28)] p-4 pr-10">
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t("pwa.dismissAria")}
            className="absolute top-3 right-3 inline-flex h-7 w-7 items-center justify-center rounded-full text-[#8A7D82] hover:text-[#2C2123] hover:bg-[#FAF6F2] transition-colors"
            data-testid="pwa-install-dismiss"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-3">
            <div
              aria-hidden="true"
              className="shrink-0 h-11 w-11 rounded-xl bg-gradient-to-br from-[#F5C7C5] to-[#9B6A6C] flex items-center justify-center shadow-[0_6px_18px_rgba(155,106,108,0.32)]"
            >
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-medium text-[#2C2123] leading-tight"
                data-testid="pwa-install-title"
              >
                {t("pwa.title")}
              </p>
              <p
                className="mt-1 text-[12px] text-[#5C4B51] leading-relaxed"
                data-testid="pwa-install-subtitle"
              >
                {t("pwa.subtitle")}
              </p>
            </div>
          </div>

          {platform === "android" && (
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={handleInstall}
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#9B6A6C] text-white px-4 py-2 text-sm font-medium hover:bg-[#805557] transition-colors shadow-[0_4px_14px_rgba(155,106,108,0.32)]"
                data-testid="pwa-install-btn"
              >
                <Plus className="h-4 w-4" />
                {t("pwa.installBtn")}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="rounded-full px-4 py-2 text-sm text-[#5C4B51] hover:text-[#2C2123] hover:bg-[#FAF6F2] transition-colors"
                data-testid="pwa-install-later-btn"
              >
                {t("pwa.notNowBtn")}
              </button>
            </div>
          )}

          {platform === "ios" && (
            <ol
              className="mt-3 space-y-2 text-[12px] text-[#2C2123]"
              data-testid="pwa-install-ios-steps"
            >
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#F8EEEE] text-[#9B6A6C] text-[10px] font-medium">
                  1
                </span>
                <span className="leading-snug flex items-center gap-1 flex-wrap">
                  {t("pwa.iosStep1Before")}
                  <Share className="h-3.5 w-3.5 text-[#9B6A6C] inline-block" aria-hidden="true" />
                  {t("pwa.iosStep1After")}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#F8EEEE] text-[#9B6A6C] text-[10px] font-medium">
                  2
                </span>
                <span className="leading-snug">{t("pwa.iosStep2")}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#F8EEEE] text-[#9B6A6C] text-[10px] font-medium">
                  3
                </span>
                <span className="leading-snug">{t("pwa.iosStep3")}</span>
              </li>
            </ol>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPwaPrompt;
